import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getCohortPlaygroundProject,
  type CohortPlaygroundProjectDetail,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { PlaygroundBody } from "./PlaygroundBody";

/** Teacher read-only view of one student's playground project. */
export default function CohortPlaygroundProjectPage() {
  const { cohortId, projectId } = useParams();
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();

  const [project, setProject] = useState<CohortPlaygroundProjectDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!bearer || !cohortId || !projectId) return;
    let cancelled = false;
    void (async () => {
      try {
        const data = await getCohortPlaygroundProject(bearer, cohortId, projectId);
        if (!cancelled) setProject(data.project);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Could not load the project");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bearer, cohortId, projectId]);

  if (error) {
    return (
      <main className="page page-align-start">
        <div className="card card-wide">
          <div className="warn-banner" role="alert">
            {error}
          </div>
          <p className="fineprint">
            <Link to={`/teacher/cohorts/${cohortId ?? ""}/playground`}>← Back to student projects</Link>
          </p>
        </div>
      </main>
    );
  }

  if (!project) {
    return (
      <main className="page page-align-start">
        <div className="card card-wide">
          <p className="muted">Loading project…</p>
        </div>
      </main>
    );
  }

  return (
    <PlaygroundBody
      eyebrow={`Teacher · Student playground · ${project.ownerEmail}`}
      title={project.title}
      backTo={`/teacher/cohorts/${cohortId ?? ""}/playground`}
      backLabel="Student projects"
      initialFiles={project.files}
      readOnly
      subtitleNote={`Read-only view of ${project.ownerEmail}'s project. You can run it; nothing is saved.`}
    />
  );
}
